import React, { useEffect, useState } from "react";
import moment from "moment";
import { Bar } from "react-chartjs-2";
import {
    Chart as ChartJS,
    BarElement,
    CategoryScale,
    LinearScale,
    Title,
    Tooltip,
    Legend,
    ChartOptions,
    ChartData,
} from "chart.js";
import {
    computeAutomatedOrderPercentLabelAndCount,
    computeBackgroundColor,
    getProductTypeForBarChartLabel,
    getSelectedValueFromEnum,
    serialize,
} from "../../../../helpers/utils/Utils";
import { useNavigate } from "react-router-dom";
import {useGetAutomatedOrderPercentBarChartMutation} from "../../../../app/utilityRequestsApi";

// Register Chart.js components
ChartJS.register(BarElement, CategoryScale, LinearScale, Title, Tooltip, Legend);

interface AutomatedOrderHistogramProps {
    period: string;
    customerId?: any
    creationDateFrom?: string;
    creationDateTo?: string;
    applySearch: boolean;
    setApplySearch: (value: boolean) => void;
    applyExport: boolean;
    setApplyExport: (value: boolean) => void;
    enums?: any,
    utilData?: any
}

const AutomatedOrderHistogram: React.FC<AutomatedOrderHistogramProps> = ({
    period,
    creationDateFrom,
    creationDateTo,
    applySearch,
    setApplySearch,
    applyExport,
    customerId,
    setApplyExport,
    enums,
    utilData
}) => {
    const [response, setResponse] = useState<any>([]);

    const [data, setData] = useState<ChartData<"bar">>({
        labels: [],
        datasets: [],
    });
    const navigate: any = useNavigate();
    const [getAutomatedOrderPercentBarChart] = useGetAutomatedOrderPercentBarChartMutation();

    const handleBarClick = (event: any, elements: any) => {
        if (elements.length > 0) {
            // Retrieve the data for the clicked bar
            const clickedElement = elements[0];

            const index = clickedElement.index;

            const isLastBarClicked = index === response.length - 1;


            let data = (response.at(index).zonedDateTime).split('T');
            let startDate = new Date(data[0] + ' 00:00:00');

            const endDate = isLastBarClicked ? creationDateTo : new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0);

            navigate({
                pathname: "/orders",
                search: serialize({
                    "createdDateFrom": moment(startDate).format("yyyy-MM-DD HH:mm:ss").toUpperCase(),
                    "createdDateTo": moment(endDate).format("yyyy-MM-DD HH:mm:ss").toUpperCase(),
                    "productStatuses": ['DELIVERED'],
                    "type": (clickedElement.datasetIndex === 0 || clickedElement.datasetIndex === 1) ? getSelectedValueFromEnum(enums, "TAX_") : getSelectedValueFromEnum(enums, "HOA_"),
                    "manualOverride": false,
                    "reordered": clickedElement.datasetIndex % 2 !== 0
                }),
                state: {
                    detail: {
                        "createdDateFrom": moment(startDate).format("yyyy-MM-DD HH:mm:ss").toUpperCase(),
                        "createdDateTo": moment(endDate).format("yyyy-MM-DD HH:mm:ss").toUpperCase(),
                        "productStatuses": ['DELIVERED'],
                        "type": (clickedElement.datasetIndex === 0 || clickedElement.datasetIndex === 1) ? getSelectedValueFromEnum(enums, "TAX_") : getSelectedValueFromEnum(enums, "HOA_"),
                        "manualOverride": false,
                        "reordered": clickedElement.datasetIndex % 2 !== 0
                    }
                }
            })

        }
    };


    const options: ChartOptions<"bar"> = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            y: {
                min: 0,
                max: 100,
                ticks: {
                    stepSize: 10,
                    callback: function (value) {
                        return value + '%';
                    },
                },
            },
        },
        plugins: {
            title: {
                display: true,
                text: "Automated Order Percentage",
                color: "black",
                font: {
                    size: 34,
                },
                padding: {
                    top: 20,
                    bottom: 20,
                },
            },
            legend: {
                display: true,
                position: "top",
            },
        },
        onClick: handleBarClick,
        onHover: function (event:any, chartElement:any) {
            const target = event.native?.target || event?.target;
            if (target instanceof HTMLCanvasElement) {
                target.style.cursor = chartElement.length ? 'pointer' : 'default';
            }
        },
    };


    useEffect(() => {
        if ((applySearch || applyExport) && enums) {
            fetchData();
        }
    }, [applySearch, applyExport, enums]);

    const fetchData = async () => {
        const query: Record<string, string | boolean | null> = {
            period,
            customerId: utilData?.customerLevel === 'CUSTOMER' ? utilData?.customer_Id : customerId || null,
            createdDateFrom: creationDateFrom ? moment(creationDateFrom).format("YYYY-MM-DD HH:mm:ss").toUpperCase() : null,
            createdDateTo: creationDateTo ? moment(creationDateTo).format("YYYY-MM-DD HH:mm:ss").toUpperCase() : null,
            setExportBtn: applyExport,
        };

        Object.keys(query).forEach((key) => query[key] === null && delete query[key]);

        try {
            const apiData: any = await getAutomatedOrderPercentBarChart(query).unwrap();
            localStorage.setItem("orderCountHistogramSearchParams", JSON.stringify(query));
            setResponse(apiData);


            let datasets = ['Tax', 'Tax Update', 'HOA', 'HOA Update'].map(label => {
                let productTypes = getProductTypeForBarChartLabel(label, enums);
                return ({
                    label: label,
                    data: computeAutomatedOrderPercentLabelAndCount(period, apiData, productTypes, label.includes('Update')),
                    backgroundColor: computeBackgroundColor(label),
                    borderWidth: 1
                });
            });

            setData({
                labels: computeAutomatedOrderPercentLabelAndCount(period, apiData, [], false, true),
                datasets: datasets,
            });
            setApplySearch(false);
        } catch (error) {
            setApplySearch(false);
            console.error("Error fetching data:", error);
        }
    };


    return (
        <>
            <div style={{ width: '100%', height: '600px' }}>
                <Bar data={data} options={options} />
            </div>
        </>
    );
};

export default AutomatedOrderHistogram;

